import {
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type QueryKey as RQQueryKey,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';
import { memosQuery } from 'networking/mindfeed/queries/memos';
import type { AnyMemo, Memo } from 'models/Memo';
import type { MemoPage } from 'networking/mindfeed/models/Memo';

type MemosData = InfiniteData<MemoPage>;

// Apply an element-wise transform to the memos of every loaded page. Returns a
// new object graph (immutable), but reuses the reference of every memo the
// transform leaves untouched, so React only re-renders the rows that changed.
const mapPages = (
  data: MemosData | undefined,
  fn: (memos: AnyMemo[]) => AnyMemo[],
): MemosData | undefined =>
  data
    ? {
        ...data,
        pages: data.pages.map((page) => ({ ...page, memos: fn(page.memos) })),
      }
    : data;

/**
 * A small, page-aware editing surface over the memos cache.
 *
 * `MemoCache` is NOT a store — it holds no data of its own. It is a thin façade
 * over a single React Query cache entry (the memos for the current
 * box/collection, keyed `[MEMOS, boxId, collectionId]`). Because that query is
 * an infinite query, the value React Query stores is an
 * `InfiniteData<MemoPage>` — a list of pages, each with its own `memos` array —
 * not a flat list. These methods read/write that one entry through
 * `queryClient.get/setQueryData`, so callers can express intent
 * (`cache.update(id, changes)`) without knowing the data is paginated.
 *
 * How it differs from the React Query cache.
 *
 * React Query owns the actual storage, keys, staleness, fetching and garbage
 * collection. `MemoCache` owns none of that — it's a stateless accessor (a
 * per-render bundle of closures), the way a repository method is an accessor
 * over a database. There is exactly one copy of the memos, in React Query; this
 * is not a second cache and adds no meaningful memory.
 *
 * How it scales
 *
 * `update`/`replace`/`remove` rebuild the pages via `map`, so each optimistic
 * edit is O(loaded memos) — but it runs once per user action (not per render),
 * copies only references, and preserves the identity of untouched memos so the
 * virtualised list re-renders just the changed row. Comfortable into the
 * thousands. Growth is bounded by pagination (30/page) on the fetch side and by
 * list virtualisation on the render side; neither is `MemoCache`'s concern. The
 * only thing that would change this is needing tens of thousands loaded at
 * once, which would call for a normalised by-id store (O(1) edits) instead.
 */
export type MemoCache = {
  // Whether a memo with this id currently exists in any loaded page.
  contains: (id: string) => boolean;
  // The memo as currently cached, if it is in a loaded page.
  get: (id: string) => AnyMemo | undefined;
  // Shallow-merge changes into the matching memo (optimistic edit).
  update: (id: string, changes: Partial<Memo>) => void;
  // Replace a memo with the server's authoritative version.
  replace: (memo: Memo) => void;
  // Add a freshly created memo to the top of the list.
  prepend: (memo: Memo) => void;
  // Drop a memo from whichever page holds it.
  remove: (id: string) => void;
};

// Build a MemoCache bound to a query client + key. Pure (no React), so it can
// be unit-tested directly against a real QueryClient.
export const createMemoCache = (
  queryClient: QueryClient,
  memosKey: RQQueryKey,
): MemoCache => {
  const edit = (fn: (memos: AnyMemo[]) => AnyMemo[]) =>
    queryClient.setQueryData<MemosData>(memosKey, (old) => mapPages(old, fn));

  const get = (id: string): AnyMemo | undefined => {
    for (const page of queryClient.getQueryData<MemosData>(memosKey)?.pages ??
      []) {
      const memo = page.memos.find((m) => m.id === id);
      if (memo) return memo;
    }
    return undefined;
  };

  return {
    contains: (id) => get(id) !== undefined,
    get,
    update: (id, changes) =>
      edit((memos) =>
        memos.map((m) => (m.id === id ? { ...m, ...changes } : m)),
      ),
    replace: (memo) =>
      edit((memos) =>
        memos.map((m) => (m.id === memo.id ? (memo as AnyMemo) : m)),
      ),
    prepend: (memo) =>
      queryClient.setQueryData<MemosData>(memosKey, (old) => {
        if (!old || old.pages.length === 0) return old;
        const [first, ...rest] = old.pages;
        return {
          ...old,
          pages: [
            { ...first, memos: [memo as AnyMemo, ...first.memos] },
            ...rest,
          ],
        };
      }),
    remove: (id) => edit((memos) => memos.filter((m) => m.id !== id)),
  };
};

// The editing surface for the memos of the current box/collection, for code
// that updates a memo outside a mutation (e.g. syncing a memo's comments
// from the comments modal).
export const useMemoCache = (): MemoCache => {
  const queryClient = useQueryClient();
  // Inside a Journal day, the list on screen is that day's.
  const {
    selectedBoxId,
    selectedCollectionId,
    journalDay: day,
  } = useActiveSpace();
  // Stable for a given selection, so effects can list it as a dependency.
  return useMemo(
    () =>
      createMemoCache(
        queryClient,
        memosQuery(selectedBoxId, { collectionId: selectedCollectionId, day })
          .queryKey,
      ),
    [queryClient, selectedBoxId, selectedCollectionId, day],
  );
};

export type MemoMutationContext = {
  // Snapshot taken before the optimistic update, used to roll back on error.
  previousMemos: MemosData | undefined;
  // The exact query key that was mutated (box + collection scoped).
  memosKey: RQQueryKey;
  queryClient: QueryClient;
  cache: MemoCache;
};

type Options<TData, TVariables> = {
  mutationFn: (variables: TVariables) => Promise<TData>;
  // Optimistic edit applied to the cache in onMutate.
  optimistic?: (cache: MemoCache, variables: TVariables) => void;
  onSuccess?: (
    data: TData,
    variables: TVariables,
    context: MemoMutationContext,
  ) => void;
  onError?: (
    error: unknown,
    variables: TVariables,
    context: MemoMutationContext | undefined,
  ) => void;
};

/**
 * Shared hook for memo mutations. Every memo mutation needs the same
 * optimistic-update lifecycle — cancel in-flight fetches, snapshot the
 * (box + collection-scoped) memos cache, apply an optimistic edit, and roll
 * back on error — so this factory owns it and each hook only declares its
 * optimistic edit and its success handling, expressed against the `MemoCache`
 * editing surface. Nothing outside this file needs to know the cache is
 * paginated.
 */
export function useOptimisticMemoMutation<TData, TVariables>({
  mutationFn,
  optimistic,
  onSuccess,
  onError,
}: Options<TData, TVariables>): UseMutationResult<
  TData,
  unknown,
  TVariables,
  MemoMutationContext
> {
  const queryClient = useQueryClient();
  // Inside a Journal day, the list on screen is that day's.
  const {
    selectedBoxId,
    selectedCollectionId,
    journalDay: day,
  } = useActiveSpace();
  const memosKey: RQQueryKey = memosQuery(selectedBoxId, {
    collectionId: selectedCollectionId,
    day,
  }).queryKey;

  const cache = createMemoCache(queryClient, memosKey);

  return useMutation<TData, unknown, TVariables, MemoMutationContext>({
    mutationFn,
    onMutate: async (variables) => {
      // Don't let an in-flight refetch clobber the optimistic update.
      await queryClient.cancelQueries({ queryKey: memosKey });
      const previousMemos = queryClient.getQueryData<MemosData>(memosKey);
      optimistic?.(cache, variables);
      return { previousMemos, memosKey, queryClient, cache };
    },
    onError: (error, variables, context) => {
      // Roll back to the pre-mutation snapshot, then run hook-specific error
      // handling (e.g. a failure toast).
      if (context) {
        queryClient.setQueryData<MemosData>(
          context.memosKey,
          context.previousMemos,
        );
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
    },
  });
}
